import { useMemo, useState } from "react";
import {
  Activity,
  AreaChart as AreaChartIcon,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Brain,
  Clock,
  Inbox,
  MessageSquare,
  Sparkles,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Spinner } from "../../../../components/ui/spinner";
import { EmptyState } from "../../../../components/ui/empty-state";
import { cn } from "../../../../lib/cn";

const METRIC_ICONS = {
  Mensagens: MessageSquare,
  Sessões: Users,
  "Mensagens p/ sessão": Activity,
  "Taxa de Compreensão": Brain,
  "Tempo Med. Resposta": Clock,
  Caracteres: Sparkles,
  "Msg. Compreendidas": Inbox,
};

const VALUE_FORMATTERS = {
  "Mensagens p/ sessão": (v) => Number(v).toFixed(2),
  "Tempo Med. Resposta": (v) => Number(v).toFixed(2) + "s",
  "Taxa de Compreensão": (v) => (Number(v) * 100).toFixed(1) + "%",
};

function formatValue(label, value) {
  if (VALUE_FORMATTERS[label]) return VALUE_FORMATTERS[label](value);
  if (typeof value === "number") return value.toLocaleString("pt-BR");
  return value;
}

function formatChange(change) {
  if (change == null) return "0%";
  const sign = change > 0 ? "+" : "";
  return `${sign}${(change * 100).toFixed(1)}%`;
}

function changeVariant(change) {
  if (change == null || Math.abs(change) < 0.001) return "neutral";
  return change > 0 ? "up" : "down";
}

export default function AssistantDashboard({ data, loading, periodLabel = "Últimos 7 dias" }) {
  const [chartType, setChartType] = useState("area");
  const [activeMetric, setActiveMetric] = useState(() => data?.graphs?.[0]?.name || null);

  const availableGraphs = useMemo(
    () => (data?.graphs || []).filter((g) => g?.points?.length > 0),
    [data]
  );

  const selectedGraph = useMemo(() => {
    if (!availableGraphs.length) return null;
    return (
      availableGraphs.find((g) => g.name === activeMetric) || availableGraphs[0]
    );
  }, [availableGraphs, activeMetric]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size={22} />
      </div>
    );
  }

  if (!data?.values?.length) {
    return (
      <EmptyState
        icon={Activity}
        title="Sem dados ainda"
        description="As métricas aparecerão aqui assim que o assistente começar a ser usado."
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.12em]"
          style={{
            background: "var(--dash-primary-soft)",
            color: "var(--dash-primary)",
            border: "1px solid var(--dash-primary-border)",
          }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: "currentColor" }}
          />
          {periodLabel}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.values.map((item, i) => (
          <KpiCard key={i} {...item} />
        ))}
      </div>

      {availableGraphs.length > 0 && (
        <div
          className="flex flex-col gap-4 rounded-2xl p-5"
          style={{
            background: "var(--dash-surface-subtle)",
            border: "1px solid var(--dash-border)",
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {availableGraphs.map((g) => {
                const Icon = METRIC_ICONS[g.name] || Activity;
                const isActive = selectedGraph?.name === g.name;
                return (
                  <button
                    key={g.name}
                    type="button"
                    onClick={() => setActiveMetric(g.name)}
                    className={cn(
                      "group flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium transition-all duration-200"
                    )}
                    style={{
                      background: isActive
                        ? "var(--dash-primary-soft)"
                        : "transparent",
                      color: isActive
                        ? "var(--dash-primary)"
                        : "var(--dash-text-muted)",
                      border: isActive
                        ? "1px solid var(--dash-primary-border)"
                        : "1px solid transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background =
                          "var(--dash-surface-hover)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Icon size={12} strokeWidth={2.2} />
                    {g.name}
                  </button>
                );
              })}
            </div>

            <div
              className="flex items-center gap-0.5 rounded-lg p-0.5"
              style={{
                background: "var(--dash-surface)",
                border: "1px solid var(--dash-border)",
              }}
            >
              <ChartTypeButton
                active={chartType === "area"}
                onClick={() => setChartType("area")}
                icon={AreaChartIcon}
                label="Linha"
              />
              <ChartTypeButton
                active={chartType === "bar"}
                onClick={() => setChartType("bar")}
                icon={BarChart3}
                label="Barras"
              />
            </div>
          </div>

          <div className="h-64 w-full">
            <DashboardChart
              points={selectedGraph?.points || []}
              chartType={chartType}
              metricLabel={selectedGraph?.name}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, changePeriod }) {
  const Icon = METRIC_ICONS[label] || Activity;
  const variant = changeVariant(changePeriod);
  const showChange = variant !== "neutral";

  return (
    <div
      className="group relative flex items-center gap-4 overflow-hidden rounded-xl p-4 transition-all duration-200"
      style={{
        background: "var(--dash-surface-subtle)",
        border: "1px solid var(--dash-border)",
      }}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
          color: "var(--dash-primary)",
        }}
      >
        <Icon size={17} strokeWidth={2} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className="truncate text-[10px] font-medium uppercase tracking-[0.1em]"
          style={{ color: "var(--dash-text-muted)" }}
          title={label}
        >
          {label}
        </span>
        <div className="flex items-baseline gap-2">
          <span
            className="truncate text-xl font-semibold tabular-nums"
            style={{ color: "var(--dash-text)", lineHeight: 1.15 }}
          >
            {formatValue(label, value)}
          </span>
          {showChange && <ChangeBadge variant={variant} value={changePeriod} />}
        </div>
      </div>
    </div>
  );
}

function ChangeBadge({ variant, value }) {
  const Icon = variant === "up" ? ArrowUpRight : ArrowDownRight;
  const color =
    variant === "up"
      ? { fg: "#1fb37c", bg: "rgba(52,254,173,0.10)" }
      : { fg: "#d61f3f", bg: "rgba(254,52,86,0.10)" };

  return (
    <span
      className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-semibold tabular-nums"
      style={{ background: color.bg, color: color.fg }}
    >
      <Icon size={9} strokeWidth={2.8} />
      {formatChange(value)}
    </span>
  );
}

function ChartTypeButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className="flex h-7 w-9 items-center justify-center rounded-md transition-all duration-150"
      style={{
        background: active ? "var(--dash-primary-soft)" : "transparent",
        color: active ? "var(--dash-primary)" : "var(--dash-text-muted)",
      }}
    >
      <Icon size={13} strokeWidth={2.2} />
    </button>
  );
}

function DashboardChart({ points, chartType, metricLabel }) {
  if (!points || points.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-xs" style={{ color: "var(--dash-text-faint)" }}>
          Sem dados no período
        </p>
      </div>
    );
  }

  const isAllZero = points.every((p) => !p.total);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0];
    return (
      <div
        className="rounded-lg px-3 py-2 text-xs"
        style={{
          background: "var(--dash-surface)",
          border: "1px solid var(--dash-border-strong)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        }}
      >
        <div
          className="text-[10px] font-medium uppercase tracking-[0.08em]"
          style={{ color: "var(--dash-text-muted)" }}
        >
          {point.payload.name}
        </div>
        <div
          className="mt-0.5 text-sm font-semibold tabular-nums"
          style={{ color: "var(--dash-text)" }}
        >
          {formatValue(metricLabel, point.value ?? 0)}
        </div>
      </div>
    );
  };

  if (chartType === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid
            vertical={false}
            stroke="var(--dash-border)"
            strokeDasharray="3 6"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: "var(--dash-text-muted)" }}
            interval="preserveStartEnd"
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "var(--dash-text-muted)" }}
            axisLine={false}
            tickLine={false}
            width={32}
            domain={isAllZero ? [0, 1] : [0, "auto"]}
          />
          <RechartsTooltip
            cursor={{ fill: "var(--dash-surface-hover)" }}
            content={renderTooltip}
          />
          <Bar
            dataKey="total"
            fill="var(--dash-primary)"
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="assistantDashGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ad34fe" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#ad34fe" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          vertical={false}
          stroke="var(--dash-border)"
          strokeDasharray="3 6"
        />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: "var(--dash-text-muted)" }}
          interval="preserveStartEnd"
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "var(--dash-text-muted)" }}
          axisLine={false}
          tickLine={false}
          width={32}
          domain={isAllZero ? [0, 1] : [0, "auto"]}
        />
        <RechartsTooltip
          cursor={{
            stroke: "var(--dash-border-strong)",
            strokeWidth: 1,
            strokeDasharray: "4 4",
          }}
          content={renderTooltip}
        />
        <Area
          type="monotone"
          dataKey="total"
          stroke="#ad34fe"
          strokeWidth={2}
          fill="url(#assistantDashGradient)"
          activeDot={{
            r: 4,
            fill: "#ad34fe",
            stroke: "var(--dash-surface)",
            strokeWidth: 2,
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
